// Common style constants for auth and form screens

export const typography = {
  // Headings
  h1: "font-plus-jakarta font-medium leading-[48px] text-[#2d2923] dark:text-white text-[36px] tracking-[-0.52px] mb-2",
  h3: "font-plus-jakarta font-medium text-[18px] text-[#2d2923] dark:text-white",
  
  // Body text
  body: "font-plus-jakarta font-normal leading-[24px] text-[#2d2923] dark:text-white text-[16px]",
  bodySmall: "font-plus-jakarta font-normal text-[12px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]",
  
  // Labels
  label: "font-plus-jakarta font-medium text-[14px] text-[#2d2923] dark:text-white mb-2",
  labelBlock: "block font-plus-jakarta font-medium text-[14px] text-[#2d2923] dark:text-white mb-2",
  
  // Links and secondary text
  link: "font-plus-jakarta font-medium text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors [text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid underline",
  secondaryText: "font-plus-jakarta font-medium text-[14px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]",
} as const

export const formStyles = {
  // Input fields
  input: "!bg-[rgba(255,255,255,0.52)] dark:!bg-transparent !border-[rgba(45,41,35,0.12)] dark:!border-[rgba(255,255,255,0.20)] placeholder:!text-[rgba(45,41,35,0.72)] dark:placeholder:!text-[rgba(255,255,255,0.48)] text-[#2d2923] dark:text-white focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:focus:!border-[rgba(255,255,255,0.32)] hover:!border-[rgba(45,41,35,0.12)] dark:hover:!border-[rgba(255,255,255,0.28)] autofill:!bg-[rgba(255,255,255,0.52)] dark:autofill:!bg-transparent autofill:!shadow-[inset_0_0_0_1000px_rgba(255,255,255,0.52)] dark:autofill:!shadow-[inset_0_0_0_1000px_transparent] [-webkit-autofill]:!bg-[rgba(255,255,255,0.52)] dark:[-webkit-autofill]:!bg-transparent",
  
  // Select fields
  select: "!bg-[rgba(255,255,255,0.52)] dark:!bg-[rgba(28,28,28,0.44)] !border-[rgba(45,41,35,0.12)] dark:!border-[#1b1919] text-[#2d2923] dark:text-[#fdfdfd] hover:!bg-[rgba(255,255,255,0.52)] dark:hover:!bg-[rgba(28,28,28,0.44)] focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:focus:!border-[#1b1919]",
  selectWhite: "!bg-white dark:!bg-[#1c1c1c] !border-[rgba(45,41,35,0.12)] dark:!border-[#1b1919] text-[#2d2923] dark:text-[#fdfdfd] hover:!bg-white dark:hover:!bg-[#1c1c1c] focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:focus:!border-[#1b1919] rounded-md",
  
  // Checkbox
  checkbox: "bg-[rgba(255,255,255,0.56)] dark:bg-[rgba(45,41,35,0.56)] border-2 border-[rgba(45,41,35,0.32)] dark:border-[rgba(255,255,255,0.32)] shrink-0 size-[20px] rounded",
  
  // Buttons
  buttonPrimary: "w-full bg-[rgba(45,41,35,0.72)] dark:bg-[#fdfdfd] !text-white dark:!text-[#2d2923] hover:bg-[#2d2923] dark:hover:bg-[rgba(253,253,253,0.9)] transition-colors font-medium",
  buttonPrimaryFlex: "flex-1 bg-[rgba(45,41,35,0.72)] dark:bg-[#fdfdfd] !text-white dark:!text-[#2d2923] hover:bg-[#2d2923] dark:hover:bg-[rgba(253,253,253,0.9)] transition-colors font-medium",
  buttonSecondary: "flex-1 bg-[rgba(255,255,255,0.56)] dark:bg-[#1c1c1c] hover:bg-[rgba(255,255,255,0.8)] dark:hover:bg-[rgba(253,253,253,0.1)] text-[#2d2923] dark:text-[#fdfdfd] border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] font-plus-jakarta font-medium",
  
  // Card
  card: "bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.4)] border-2 border-solid border-white dark:border-[#1b1919] p-8 rounded-xl shadow-xl dark:shadow-none relative overflow-hidden",
  
  // Card without shadow (for dashboard blocks)
  cardNoShadow: "bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.4)] border border-solid border-white dark:border-[#1b1919] p-6 rounded-xl relative overflow-hidden",
} as const

export const colors = {
  text: {
    primary: "#2d2923",
    secondary: "rgba(45,41,35,0.72)",
    muted: "rgba(45,41,35,0.72)",
  },
  background: {
    page: "#f2f1f6",
    card: "rgba(255,255,255,0.32)",
    input: "rgba(255,255,255,0.52)",
  },
  border: {
    default: "rgba(45,41,35,0.12)",
    checkbox: "rgba(45,41,35,0.32)",
  },
} as const

// Dark theme colors from Figma
export const darkColors = {
  text: {
    primary: "#fdfdfd",
    secondary: "rgba(253,253,253,0.72)",
    muted: "rgba(253,253,253,0.72)",
    placeholder: "rgba(253,253,253,0.48)",
  },
  background: {
    page: "#0d0d0d",
    card: "#1c1c1c",
    cardTransparent: "rgba(28,28,28,0.4)",
    cardSemi: "rgba(28,28,28,0.7)",
    input: "rgba(28,28,28,0.44)",
    graph: "rgba(28,28,28,0.48)",
    container: "rgba(253,253,253,0.08)",
    hover: "rgba(253,253,253,0.1)",
    icon: "rgba(253,253,253,0.2)",
  },
  border: {
    default: "#1b1919",
    card: "#1b1919",
    subtle: "rgba(253,253,253,0.12)",
    active: "#fdfdfd",
  },
  accent: {
    lime: "#e3fd5a",
    limeLight: "#dffb2c",
    limeTransparent: "rgba(227,253,90,0.28)",
  },
  status: {
    success: "#1fb845",
    error: "#ff540c",
    hot: "#ff0eb7",
    trending: "#0e4fff",
    profitable: "#24b60d",
    trendingUp: "#2ea66b",
  },
  tags: {
    purple: "#ac5bdb",
    blue: "#3481ec",
  },
  chart: {
    gray: "#bfbcbc",
    grayDark: "#b1b0b0",
    graphLine: "#878787",
  },
  header: {
    bg: "#2D2923",
    menuText: "#FFFFFF",
    iconBg: "rgba(255,255,255,0.80)",
    avatarBg: "#ecebef",
  },
  projectIcons: {
    green: "#6cb211",
    cyan: "#1197b2",
    yellow: "#b29d11",
    pink: "#e461ce",
  },
} as const

